import React, { useState, useMemo, useCallback, useRef } from "react";
import {
  ChevronLeft,
  ChevronRight,
  X,
  Calendar,
  Download,
  Clock,
  User,
  FileText,
  Filter,
  CalendarClock,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { toast } from "sonner";
import * as XLSX from "xlsx";
import {
  // useGetManagerOvertimeRequestsQuery,
  useGetManagerDepartmentRequestsQuery,
  useApproveOvertimeRequestMutation,
  useRejectOvertimeByManagerMutation,
} from "../../slices/overtimeApiSlice";
import { useGetDivisionsQuery } from "../../slices/userApiSlice";
import CustomSelect from "../../components/CustomSelect/CustomSelect";
import DateRangePicker from "../../components/DateRangePicker/DateRangePicker";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { Label } from "../../components/ui/label";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "../../components/ui/pagination";

export default function OvertimeRequests() {
  const navigate = useNavigate();
  const { userInfo } = useSelector((state) => state.auth);
  const academyId = userInfo?.academyId;

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Selected record for detail modal
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [managerNote, setManagerNote] = useState("");

  // Status filter
  const [status, setStatus] = useState("All");
  // Division filter
  const [selectedDivisionId, setSelectedDivisionId] = useState("all");
  // Date range state (default to last 1 week)
  const getTodayInThimphu = () => {
    const now = new Date();
    const thimphuDate = new Date(
      now.toLocaleString("en-US", { timeZone: "Asia/Thimphu" }),
    );
    thimphuDate.setHours(0, 0, 0, 0);
    return thimphuDate;
  };

  const today = getTodayInThimphu();
  const oneWeekAgo = new Date(today);
  oneWeekAgo.setDate(today.getDate() - 6); // 7 days including today

  // Helper function to format date as YYYY-MM-DD without timezone issues
  const formatDateString = (date) => {
    if (!date) return null;
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const [startDate, setStartDate] = useState(formatDateString(oneWeekAgo));
  const [endDate, setEndDate] = useState(formatDateString(today));
  const [showDateRangePicker, setShowDateRangePicker] = useState(false);
  const dateRangeButtonRef = useRef(null);

  // Export-related state
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportMonthYear, setExportMonthYear] = useState(null);
  const [selectedTechnicianForExport, setSelectedTechnicianForExport] =
    useState("all");
  const [isExporting, setIsExporting] = useState(false);

  // Fetch divisions - shared across all academies
  const { data: divisionsData } = useGetDivisionsQuery();
  const divisions = divisionsData || [];

  // Prepare division options for CustomSelect
  const divisionOptions = useMemo(() => {
    return divisions.map((division) => ({
      value: division.divisionId || division._id,
      label: division.name,
      divisionId: division.divisionId || division._id,
      name: division.name,
    }));
  }, [divisions]);

  // Fetch overtime requests from API
  const { data, isLoading, isError, error, refetch } =
    useGetManagerDepartmentRequestsQuery();

  // Manager action mutations
  const [approveRequest, { isLoading: isApproving }] =
    useApproveOvertimeRequestMutation();
  const [rejectRequest, { isLoading: isRejecting }] =
    useRejectOvertimeByManagerMutation();

  // Helper to format backend ISO date string
  function formatBackendDate(iso) {
    const d = new Date(iso);
    return `${d.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    })} - ${d.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    })}`;
  }

  // Format note timestamp
  function formatNoteTimestamp(timestamp) {
    if (!timestamp) return "";
    try {
      const date = new Date(timestamp);
      // Add 6 hours to adjust timezone (same as Submitted On logic)
      date.setHours(date.getHours() + 6);

      const dateStr = date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
      const timeStr = date.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
      return `Note added on: ${dateStr}, ${timeStr}`;
    } catch (error) {
      return "";
    }
  }

  // Format date for submission timestamp
  function formatSubmittedDate(dateString) {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      // Add 6 hours to adjust timezone
      date.setHours(date.getHours() + 6);

      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    } catch (error) {
      return dateString;
    }
  }

  // Format date and time for submission timestamp
  function formatSubmittedDateTime(dateString) {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      // Add 6 hours to adjust timezone
      date.setHours(date.getHours() + 6);

      const dateStr = date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
      const timeStr = date.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
      return `${dateStr} at ${timeStr}`;
    } catch (error) {
      return dateString;
    }
  }

  // Map backend data to table rows
  const requests = useMemo(() => {
    // Handle both response structures:
    // 1. Grouped: { forwarded: [], approved: [], rejected: [] }
    // 2. Flat array: [{ status: 'REJECTED', ... }]
    let allItems = [];

    if (data) {
      // Check if data is a grouped response
      if (data.forwarded || data.approved || data.rejected) {
        if (data.forwarded && Array.isArray(data.forwarded)) {
          allItems = allItems.concat(
            data.forwarded.map((item) => ({
              ...item,
              status: item.status || "FORWARDED",
            })),
          );
        }
        if (data.approved && Array.isArray(data.approved)) {
          allItems = allItems.concat(
            data.approved.map((item) => ({
              ...item,
              status: item.status || "APPROVED",
            })),
          );
        }
        if (data.rejected && Array.isArray(data.rejected)) {
          allItems = allItems.concat(
            data.rejected.map((item) => ({
              ...item,
              status: item.status || "REJECTED",
            })),
          );
        }
      }
      // Otherwise treat data as a flat array
      else if (Array.isArray(data)) {
        allItems = data;
      }
    }

    if (!Array.isArray(allItems) || allItems.length === 0) return [];

    const statusColorMap = {
      APPROVED: "bg-blue-100 text-blue-700",
      Approved: "bg-blue-100 text-blue-700",
      REJECTED: "bg-red-100 text-red-700",
      Rejected: "bg-red-100 text-red-700",
      PENDING: "bg-yellow-100 text-yellow-700",
      Pending: "bg-yellow-100 text-yellow-700",
      FORWARDED: "bg-gray-100 text-gray-700",
      Forwarded: "bg-gray-100 text-gray-700",
      REAPPLY: "bg-orange-100 text-orange-700",
      "Re-Apply": "bg-orange-100 text-orange-700",
      REAPPLIED: "bg-orange-100 text-orange-700",
      "Re-Applied": "bg-orange-100 text-orange-700",
    };

    const statusDisplay = {
      PENDING: "Pending",
      REAPPLY: "Re-Apply",
      REAPPLIED: "Re-Applied",
      REJECTED: "Rejected",
      FORWARDED: "Forwarded",
      APPROVED: "Approved",
    };

    return allItems
      .filter((item) => item && (item.details || item.startTime)) // Filter out null items
      .map((item) => {
        const displayStatus =
          statusDisplay[item.status] || item.status || "Pending";

        // Handle both new structure (with details object) and fallback structure
        const details = item.details || item;

        // Extract supervisor note and supervisor email from notes array
        let supervisorNote = "";
        let supervisorEmail = "";
        let supervisorTimestamp = "";
        let managerNote = "";
        let managerEmail = "";
        let managerTimestamp = "";
        let isDirectSupervisorAction = false; // Flag for direct supervisor rejection

        if (
          details.notes &&
          Array.isArray(details.notes) &&
          details.notes.length > 0
        ) {
          // Go through notes to find supervisor and manager notes
          // Use separate tracking to ensure we get LAST of each type
          let lastSupervisorNote = null;
          let lastManagerNote = null;

          details.notes.forEach((note) => {
            // Check action field - this is the most reliable indicator
            if (note.action === "REJECTED") {
              // Manager rejection - uses the supervisor field to store manager email
              lastManagerNote = note;
            } else if (note.action === "FORWARDED") {
              // Supervisor forward
              lastSupervisorNote = note;
            }
            // Fallback: check recordedBy field if action isn't present
            else if (note.recordedBy === "MANAGER") {
              lastManagerNote = note;
            } else if (note.recordedBy === "SUPERVISOR") {
              lastSupervisorNote = note;
            }
            // Fallback: check email fields - only if we haven't already identified this note
            else if (note.managerEmail && !lastManagerNote) {
              lastManagerNote = note;
            } else if (note.supervisorEmail && !lastSupervisorNote) {
              lastSupervisorNote = note;
            }
          });

          // Extract supervisor data (from FORWARDED action)
          if (lastSupervisorNote) {
            supervisorNote =
              lastSupervisorNote.note || lastSupervisorNote.reason || "";
            // Supervisor email can be in supervisorEmail or supervisor field
            supervisorEmail = (
              lastSupervisorNote.supervisorEmail ||
              lastSupervisorNote.supervisor ||
              ""
            )
              .replace(/@company\.com/g, "")
              .replace(/@company\.com@company\.com/g, "@company.com"); // Fix double @company.com
            supervisorTimestamp =
              lastSupervisorNote.createdAt ||
              lastSupervisorNote.recordedAt ||
              lastSupervisorNote.timestamp ||
              "";
          }

          // Extract manager data (from REJECTED action)
          if (lastManagerNote) {
            managerNote = lastManagerNote.note || lastManagerNote.reason || "";
            // For REJECTED action, manager email is in the supervisor field
            managerEmail = (
              lastManagerNote.supervisorEmail ||
              lastManagerNote.supervisor ||
              lastManagerNote.managerEmail ||
              ""
            )
              .replace(/@company\.com/g, "")
              .replace(/@company\.com@company\.com/g, "@company.com"); // Fix double @company.com
            managerTimestamp =
              lastManagerNote.createdAt ||
              lastManagerNote.recordedAt ||
              lastManagerNote.timestamp ||
              "";

            // Check if this is a direct supervisor action by examining the email
            // If the email contains "supervisor" in it and there's no prior forward action, it's a direct supervisor rejection
            if (
              !lastSupervisorNote &&
              managerEmail &&
              managerEmail.toLowerCase().includes("supervisor")
            ) {
              isDirectSupervisorAction = true;
              // Move manager note to supervisor note for display purposes
              supervisorNote = managerNote;
              supervisorEmail = managerEmail;
              supervisorTimestamp = managerTimestamp;
              managerNote = "";
              managerEmail = "";
              managerTimestamp = "";
            }
          }
        }

        // Flag to indicate if this is a direct manager action (no supervisor involved)
        const isDirectManagerAction =
          !supervisorNote &&
          managerNote &&
          (displayStatus === "Rejected" || displayStatus === "Approved");

        return {
          id: details.id || item.overtimeId || item.id,
          employee:
            details.userName || item.employeeName || item.userId || "N/A",
          email: details.userEmail || item.email || item.employeeEmail || "N/A",
          from: formatBackendDate(details.startTime),
          to: formatBackendDate(details.endTime),
          description: details.description || "No description",
          hours: formatHours(details.totalHours || 0),
          totalHours: details.totalHours || 0,
          status: displayStatus,
          statusColor:
            statusColorMap[item.status] || "bg-gray-100 text-gray-700",
          supervisorNote: supervisorNote,
          supervisorEmail: supervisorEmail,
          supervisorTimestamp: supervisorTimestamp,
          managerNote: managerNote,
          managerEmail: managerEmail,
          managerTimestamp: managerTimestamp,
          isDirectManagerAction: isDirectManagerAction,
          isDirectSupervisorAction: isDirectSupervisorAction,
          submissionDate:
            details.submittedAt ||
            details.createdAt ||
            details.recordedDate ||
            new Date().toISOString(),
          divisionId: details.divisionId || item.divisionId,
        };
      });
  }, [data]);

  // Date picker helpers
  const formatDateDisplay = (date) => {
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  // Helper function to format date for display
  const formatDate = (dateString, short = false) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    if (short) {
      // dd/mm/yyyy format for compact display
      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    }
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  // Filtered records based on status, division, and date range
  const filteredRecords = useMemo(() => {
    const backendStatusMap = {
      Pending: "PENDING",
      "Re-Apply": "REAPPLY",
      "Re-Applied": "REAPPLIED",
      Rejected: "REJECTED",
      Forwarded: "FORWARDED",
      Approved: "APPROVED",
    };

    let filtered = requests.filter((item) => {
      const itemBackendStatus = backendStatusMap[item.status] || item.status;
      if (status !== "All" && itemBackendStatus !== status) return false;

      // Filter by division
      if (
        selectedDivisionId &&
        selectedDivisionId !== "all" &&
        item.divisionId !== selectedDivisionId
      ) {
        return false;
      }

      return true;
    });

    // Apply date range filter only if both dates are selected
    if (startDate && endDate) {
      filtered = filtered.filter((item) => {
        try {
          // Check date range - parse "Jan 1, 2025" format from item.from
          const dateStr = item.from?.split(" - ")[0];
          if (!dateStr) return false;

          const itemDate = new Date(dateStr);
          if (isNaN(itemDate.getTime())) return false; // Invalid date

          itemDate.setHours(0, 0, 0, 0);
          const filterStart = new Date(startDate);
          filterStart.setHours(0, 0, 0, 0);
          const filterEnd = new Date(endDate);
          filterEnd.setHours(23, 59, 59, 999);

          const isInRange = itemDate >= filterStart && itemDate <= filterEnd;
          return isInRange;
        } catch (err) {
          return false;
        }
      });
    }

    // Sort by latest submission date first (most recent requests first)
    // Use stable sort with ID as tiebreaker to ensure consistent ordering
    return filtered.sort((a, b) => {
      const dateA = new Date(a.submissionDate);
      const dateB = new Date(b.submissionDate);
      const timeDiff = dateB - dateA;
      // If timestamps are equal, use ID comparison for stability
      if (timeDiff === 0) {
        return (b.id || "").toString().localeCompare((a.id || "").toString());
      }
      return timeDiff;
    });
  }, [requests, status, selectedDivisionId, startDate, endDate]);

  // Pagination
  const totalPages = Math.ceil(filteredRecords.length / itemsPerPage);
  const totalResults = filteredRecords.length;
  const paginatedRecords = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredRecords.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredRecords, currentPage]);

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  // Get unique technicians from filtered records (approved status only)
  const technicianOptionsForExport = useMemo(() => {
    const approvedRecords = filteredRecords.filter(
      (item) => item.status === "Approved",
    );

    // Create a map to store unique technicians by email (email is unique identifier)
    const technicianMap = new Map();
    approvedRecords.forEach((record) => {
      const key = record.email.toLowerCase();
      if (!technicianMap.has(key)) {
        technicianMap.set(key, {
          userId: record.id,
          email: record.email,
          name: record.employee,
        });
      }
    });

    // Convert to array and sort by name
    return Array.from(technicianMap.values()).sort((a, b) =>
      a.name.localeCompare(b.name),
    );
  }, [filteredRecords]);

  // Format hours - show minutes if not a whole hour
  function formatHours(totalHours) {
    if (!totalHours || totalHours === 0) return "0 hrs";

    const hours = Math.floor(totalHours);
    const minutes = Math.round((totalHours - hours) * 60);

    if (hours === 0) {
      return `${minutes} min`;
    } else if (minutes === 0) {
      return `${hours} hr${hours !== 1 ? "s" : ""}`;
    } else {
      return `${hours} hr${hours !== 1 ? "s" : ""} ${minutes} min`;
    }
  }

  // Export handler
  const handleExportApprovedOvertimes = async () => {
    // Validate month selection
    if (!exportMonthYear) {
      toast.warning("Month Required: Please select a month to export");
      return;
    }

    // Get approved records from filtered data
    const approvedRecords = filteredRecords.filter(
      (item) => item.status === "Approved",
    );

    if (approvedRecords.length === 0) {
      toast.info(
        "No Data: No approved overtime records found for the selected filters",
      );
      return;
    }

    // Further filter by the selected export month
    const exportMonth = new Date(exportMonthYear);
    const monthStart = new Date(
      exportMonth.getFullYear(),
      exportMonth.getMonth(),
      1,
    );
    const monthEnd = new Date(
      exportMonth.getFullYear(),
      exportMonth.getMonth() + 1,
      0,
    );

    let monthFilteredRecords = approvedRecords.filter((record) => {
      try {
        const dateStr = record.from?.split(" - ")[0];
        if (!dateStr) return false;

        const recordDate = new Date(dateStr);
        if (isNaN(recordDate.getTime())) return false;

        recordDate.setHours(0, 0, 0, 0);
        return recordDate >= monthStart && recordDate <= monthEnd;
      } catch (err) {
        return false;
      }
    });

    if (monthFilteredRecords.length === 0) {
      toast.info(
        `No Data for Month: No approved overtime records found for ${exportMonth.toLocaleDateString(
          "en-US",
          { month: "long", year: "numeric" },
        )}`,
      );
      return;
    }

    setIsExporting(true);

    try {
      // Group records by userId and email - this will consolidate all records for the same technician
      const groupedByTechnician = {};
      monthFilteredRecords.forEach((record) => {
        const key = record.email.toLowerCase();
        if (!groupedByTechnician[key]) {
          groupedByTechnician[key] = {
            userId: record.id,
            email: record.email,
            name: record.employee,
            records: [],
          };
        }
        groupedByTechnician[key].records.push(record);
      });

      let technicianGroups = Object.values(groupedByTechnician);

      // If a specific technician is selected, filter to only that technician's records
      if (selectedTechnicianForExport !== "all") {
        technicianGroups = technicianGroups.filter(
          (group) => group.userId === selectedTechnicianForExport,
        );
      }

      if (technicianGroups.length === 0) {
        setIsExporting(false);
        toast.info("No Data: No records found for the selected technician");
        return;
      }

      // Create separate files for each technician
      technicianGroups.forEach((group) => {
        const excelData = group.records.map((record, index) => {
          const dateAndTime = record.from.split(" - ");
          const date = dateAndTime[0] || "";
          const fromTime = dateAndTime[1] || "";
          const toTime = record.to.split(" - ")[1] || "";

          return {
            "No.": index + 1,
            "From Date/Time": date + " " + fromTime,
            "Number of Hours": formatHours(record.totalHours),
            "To Date/Time": date + " " + toTime,
            "Hourly Rate": "",
            Amount: "",
          };
        });

        const workbook = XLSX.utils.book_new();
        const worksheet = XLSX.utils.json_to_sheet(excelData);

        worksheet["!cols"] = [
          { wch: 8 },
          { wch: 25 },
          { wch: 18 },
          { wch: 25 },
          { wch: 15 },
          { wch: 15 },
        ];

        XLSX.utils.book_append_sheet(workbook, worksheet, "Overtime Details");

        const monthYearStr = exportMonth
          .toLocaleDateString("en-US", { month: "short", year: "numeric" })
          .replace(" ", "-");
        XLSX.writeFile(workbook, `Overtime_${group.name}_${monthYearStr}.xlsx`);
      });

      setShowExportModal(false);
      setExportMonthYear(null);
      setSelectedTechnicianForExport("all");
      setIsExporting(false);

      toast.success(
        `Exported Successfully! ${
          selectedTechnicianForExport !== "all"
            ? "1 file"
            : technicianGroups.length + " files"
        } downloaded`,
      );
    } catch (error) {
      setIsExporting(false);
      toast.error("Export Error: Failed to export overtime records");
    }
  };

  // Handle approve action
  const handleApprove = async () => {
    if (!managerNote.trim()) {
      toast.warning(
        "Approval Note Required: Please add a note before approving",
      );
      return;
    }

    try {
      // Save data before clearing state to prevent double-clicks
      const requestId = selectedRecord.id;
      const approvalNoteText = managerNote;

      // Close modal immediately to prevent double-clicks
      setSelectedRecord(null);
      setManagerNote("");

      await approveRequest({
        requestId: requestId,
        approvalNote: approvalNoteText,
      }).unwrap();

      // Refresh the data after successful approval
      await refetch();

      toast.success(
        "Approved! Overtime request has been approved successfully",
      );
    } catch (error) {
      toast.error(error?.data?.error || "Failed to approve overtime request");
    }
  };

  // Handle reject action
  const handleReject = async () => {
    if (!managerNote.trim()) {
      toast.warning(
        "Rejection Note Required: Please add a reason before rejecting",
      );
      return;
    }

    try {
      // Save data before clearing state to prevent double-clicks
      const requestId = selectedRecord.id;
      const rejectionReason = managerNote;

      // Close modal immediately to prevent double-clicks
      setSelectedRecord(null);
      setManagerNote("");

      await rejectRequest({
        requestId: requestId,
        rejectionNote: rejectionReason,
      }).unwrap();

      // Refresh the data after successful rejection
      await refetch();

      toast.success("Rejected! Overtime request has been rejected");
    } catch (error) {
      toast.error(error?.data?.error || "Failed to reject overtime request");
    }
  };

  return (
    <div className="p-4 sm:p-6 space-y-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
        <div className="flex items-center gap-3 sm:gap-4">
          <Button
            onClick={() => navigate(-1)}
            variant="ghost"
            size="icon"
            className="h-9 w-9 sm:h-10 sm:w-10 rounded-full"
            aria-label="Go back"
          >
            <ChevronLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-zinc-900">
              Overtime Requests
            </h1>
            <p className="text-sm text-zinc-600 mt-1">
              Manage and review overtime requests from your department
            </p>
          </div>
        </div>
        <Button
          onClick={() => setShowExportModal(true)}
          variant="outline"
          className="gap-2 shadow-sm"
        >
          <Download className="h-4 w-4" />
          <span>Export Records</span>
        </Button>
      </div>

      {/* Records Section */}
      <Card className="shadow-sm">
        <CardHeader className="border-b bg-white">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div>
              <CardTitle className="text-xl font-bold text-zinc-900 flex items-center gap-2">
                <CalendarClock className="h-5 w-5 text-primary" />
                Records
              </CardTitle>
              <p className="text-sm text-zinc-600 mt-1">
                All overtime requests from your department
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
              {/* Status Filter */}
              <Select
                value={status}
                onValueChange={(value) => {
                  setStatus(value);
                  setCurrentPage(1);
                }}
              >
                <SelectTrigger className="w-full sm:w-[150px]">
                  <SelectValue placeholder="All Statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="All">All Statuses</SelectItem>
                  <SelectItem value="PENDING">Pending</SelectItem>
                  <SelectItem value="APPROVED">Approved</SelectItem>
                  <SelectItem value="REJECTED">Rejected</SelectItem>
                  <SelectItem value="FORWARDED">Forwarded</SelectItem>
                </SelectContent>
              </Select>

              {/* Division Filter */}
              <Select
                value={selectedDivisionId}
                onValueChange={(value) => {
                  setSelectedDivisionId(value);
                  setCurrentPage(1);
                }}
              >
                <SelectTrigger className="w-full sm:w-[180px]">
                  <SelectValue placeholder="All Divisions" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Divisions</SelectItem>
                  {divisions.map((division) => (
                    <SelectItem
                      key={division.divisionId || division._id}
                      value={division.divisionId || division._id}
                    >
                      {division.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Date Range Picker */}
              <div className="relative">
                <Button
                  ref={dateRangeButtonRef}
                  onClick={() => setShowDateRangePicker(!showDateRangePicker)}
                  variant="outline"
                  className="w-full sm:w-auto gap-2"
                >
                  <Calendar className="h-4 w-4" />
                  {startDate && endDate ? (
                    <>
                      <span className="hidden lg:inline">
                        {formatDate(startDate)} - {formatDate(endDate)}
                      </span>
                      <span className="lg:hidden">
                        {formatDate(startDate, true)} -{" "}
                        {formatDate(endDate, true)}
                      </span>
                    </>
                  ) : (
                    "Select date range"
                  )}
                </Button>
                <DateRangePicker
                  show={showDateRangePicker}
                  onClose={() => setShowDateRangePicker(false)}
                  startDate={startDate}
                  endDate={endDate}
                  onDateRangeSelect={({ start, end }) => {
                    if (start && end) {
                      setStartDate(start);
                      setEndDate(end);
                    }
                  }}
                  buttonRef={dateRangeButtonRef}
                  allowFutureDates={true}
                />
              </div>
            </div>
          </div>
        </CardHeader>

        {/* Table */}
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            {isLoading ? (
              <div className="p-8 text-center">
                <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent mb-3"></div>
                <p className="text-sm text-muted-foreground">
                  Loading overtime requests...
                </p>
              </div>
            ) : isError ? (
              <div className="p-8 text-center">
                <p className="text-red-600 font-semibold mb-2">
                  Failed to load requests
                </p>
                <p className="text-sm text-muted-foreground">
                  {error?.status && `Error: ${error.status}`}
                  {error?.data?.message && ` - ${error.data.message}`}
                </p>
              </div>
            ) : paginatedRecords.length === 0 ? (
              <div className="p-8 text-center">
                <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-3 opacity-50" />
                <p className="text-sm text-muted-foreground">
                  {requests.length === 0
                    ? "No overtime requests found"
                    : "No overtime requests match the selected filters"}
                </p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <TableHead className="font-semibold">User</TableHead>
                    <TableHead className="font-semibold">Date</TableHead>
                    <TableHead className="font-semibold">From</TableHead>
                    <TableHead className="font-semibold">To</TableHead>
                    <TableHead className="font-semibold">Hours</TableHead>
                    <TableHead className="font-semibold">Description</TableHead>
                    <TableHead className="font-semibold">Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginatedRecords.map((item, index) => (
                    <TableRow
                      key={item.id || index}
                      className="cursor-pointer hover:bg-muted/30 transition-colors"
                      onClick={() => setSelectedRecord(item)}
                    >
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="h-9 w-9 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                            <User className="h-4 w-4 text-primary" />
                          </div>
                          <div>
                            <div className="font-medium text-sm">
                              {item.employee}
                            </div>
                            <div className="text-xs text-muted-foreground">
                              {item.email}
                            </div>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="font-medium">
                        {item.from.split(" - ")[0]}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {item.from.split(" - ")[1]
                          ? `${item.from.split(" - ")[1].substring(0, 5)} ${item.from.split(" - ")[1].substring(9, 11)}`
                          : "-"}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {item.to.split(" - ")[1]
                          ? `${item.to.split(" - ")[1].substring(0, 5)} ${item.to.split(" - ")[1].substring(9, 11)}`
                          : "-"}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1.5 text-sm">
                          <Clock className="h-3.5 w-3.5 text-muted-foreground" />
                          {item.hours}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div
                          className="max-w-xs truncate"
                          title={item.description}
                        >
                          {item.description.length > 40
                            ? `${item.description.substring(0, 40)}...`
                            : item.description}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant={
                            item.status === "Approved"
                              ? "default"
                              : item.status === "Rejected"
                                ? "destructive"
                                : item.status === "Pending"
                                  ? "secondary"
                                  : "outline"
                          }
                          className={
                            item.status === "Approved"
                              ? "bg-blue-100 text-blue-700 hover:bg-blue-100"
                              : item.status === "Rejected"
                                ? "bg-red-100 text-red-700 hover:bg-red-100"
                                : item.status === "Pending"
                                  ? "bg-yellow-100 text-yellow-700 hover:bg-yellow-100"
                                  : "bg-gray-100 text-gray-700 hover:bg-gray-100"
                          }
                        >
                          {item.status}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </CardContent>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="border-t p-4">
            <Pagination>
              <PaginationContent>
                <PaginationItem>
                  <PaginationPrevious
                    onClick={() =>
                      handlePageChange(Math.max(1, currentPage - 1))
                    }
                    className={
                      currentPage === 1
                        ? "pointer-events-none opacity-50"
                        : "cursor-pointer"
                    }
                  />
                </PaginationItem>

                {Array.from({ length: Math.min(7, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 7) {
                    pageNum = i + 1;
                  } else if (currentPage <= 4) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 3) {
                    pageNum = totalPages - 6 + i;
                  } else {
                    pageNum = currentPage - 3 + i;
                  }
                  return (
                    <PaginationItem key={pageNum}>
                      <PaginationLink
                        onClick={() => handlePageChange(pageNum)}
                        isActive={currentPage === pageNum}
                        className="cursor-pointer"
                      >
                        {pageNum}
                      </PaginationLink>
                    </PaginationItem>
                  );
                })}

                {totalPages > 7 && currentPage < totalPages - 3 && (
                  <>
                    <PaginationItem>
                      <PaginationEllipsis />
                    </PaginationItem>
                    <PaginationItem>
                      <PaginationLink
                        onClick={() => handlePageChange(totalPages)}
                        className="cursor-pointer"
                      >
                        {totalPages}
                      </PaginationLink>
                    </PaginationItem>
                  </>
                )}

                <PaginationItem>
                  <PaginationNext
                    onClick={() =>
                      handlePageChange(Math.min(totalPages, currentPage + 1))
                    }
                    className={
                      currentPage === totalPages
                        ? "pointer-events-none opacity-50"
                        : "cursor-pointer"
                    }
                  />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
            <div className="text-sm text-muted-foreground text-center mt-2">
              Showing {(currentPage - 1) * itemsPerPage + 1} to{" "}
              {Math.min(currentPage * itemsPerPage, totalResults)} of{" "}
              {totalResults} results
            </div>
          </div>
        )}
      </Card>

      {/* Export Modal */}
      <Dialog open={showExportModal} onOpenChange={setShowExportModal}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Download className="h-5 w-5 text-primary" />
              Export Overtime Records
            </DialogTitle>
            <DialogDescription>
              Export approved overtime records for a specific month
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Month Selection */}
            <div className="space-y-2">
              <Label htmlFor="export-month" className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-muted-foreground" />
                Select Month <span className="text-red-500">*</span>
              </Label>
              <Input
                id="export-month"
                type="month"
                value={
                  exportMonthYear
                    ? `${exportMonthYear.getFullYear()}-${String(
                        exportMonthYear.getMonth() + 1,
                      ).padStart(2, "0")}`
                    : ""
                }
                onChange={(e) => {
                  if (e.target.value) {
                    const [year, month] = e.target.value.split("-");
                    setExportMonthYear(
                      new Date(parseInt(year), parseInt(month) - 1, 1),
                    );
                  } else {
                    setExportMonthYear(null);
                  }
                }}
                min={`${new Date().getFullYear()}-01`}
                max={`${new Date().getFullYear()}-${String(
                  new Date().getMonth() + 1,
                ).padStart(2, "0")}`}
              />
              <p className="text-xs text-muted-foreground">
                Only approved records from the selected month will be exported
              </p>
            </div>

            {/* Technician Selection */}
            {technicianOptionsForExport.length > 0 && (
              <div className="space-y-2">
                <Label
                  htmlFor="export-technician"
                  className="flex items-center gap-2"
                >
                  <User className="h-4 w-4 text-muted-foreground" />
                  Select Technician
                </Label>
                <Select
                  value={selectedTechnicianForExport}
                  onValueChange={setSelectedTechnicianForExport}
                >
                  <SelectTrigger id="export-technician">
                    <SelectValue placeholder="Select technician" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">
                      All Technicians ({technicianOptionsForExport.length})
                    </SelectItem>
                    {technicianOptionsForExport.map((tech) => (
                      <SelectItem
                        key={`${tech.userId}-${tech.email}`}
                        value={tech.userId}
                      >
                        {tech.name} ({tech.email})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  {selectedTechnicianForExport === "all"
                    ? `Will create ${technicianOptionsForExport.length} separate Excel files`
                    : "Will create 1 Excel file for this technician"}
                </p>
              </div>
            )}

            {technicianOptionsForExport.length === 0 && (
              <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg flex items-start gap-3">
                <FileText className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-yellow-800">
                    No approved records found
                  </p>
                  <p className="text-xs text-yellow-700 mt-1">
                    Adjust your filters to see approved overtime records
                  </p>
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowExportModal(false);
                setExportMonthYear(null);
                setSelectedTechnicianForExport("all");
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleExportApprovedOvertimes}
              disabled={
                !exportMonthYear ||
                isExporting ||
                technicianOptionsForExport.length === 0
              }
              className="gap-2"
            >
              {isExporting ? (
                <>
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-background border-t-transparent"></div>
                  Exporting...
                </>
              ) : (
                <>
                  <Download className="h-4 w-4" />
                  Export
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail Modal */}
      <Dialog
        open={!!selectedRecord}
        onOpenChange={(open) => {
          if (!open) {
            setSelectedRecord(null);
            setManagerNote("");
          }
        }}
      >
        <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
          <DialogHeader className="border-b pb-4">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-3">
                  <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <User className="h-6 w-6 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <DialogTitle className="text-xl truncate">
                      {selectedRecord?.userName || selectedRecord?.employee}
                    </DialogTitle>
                    <p className="text-sm text-muted-foreground truncate">
                      {selectedRecord?.userEmail || selectedRecord?.email}
                    </p>
                  </div>
                </div>
                {selectedRecord && (
                  <Badge
                    variant={
                      selectedRecord.status === "Approved"
                        ? "default"
                        : selectedRecord.status === "Rejected"
                          ? "destructive"
                          : selectedRecord.status === "Pending"
                            ? "secondary"
                            : "outline"
                    }
                    className={
                      selectedRecord.status === "Approved"
                        ? "bg-blue-100 text-blue-700 hover:bg-blue-100"
                        : selectedRecord.status === "Rejected"
                          ? "bg-red-100 text-red-700 hover:bg-red-100"
                          : selectedRecord.status === "Pending"
                            ? "bg-yellow-100 text-yellow-700 hover:bg-yellow-100"
                            : "bg-gray-100 text-gray-700 hover:bg-gray-100"
                    }
                  >
                    {selectedRecord.status}
                  </Badge>
                )}
              </div>
            </div>
          </DialogHeader>

          {selectedRecord && (
            <div className="space-y-4 py-4">
              {/* Description */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2 text-primary">
                  <FileText className="h-4 w-4" />
                  Description
                </Label>
                <p className="text-sm text-muted-foreground bg-muted/50 p-3 rounded-lg">
                  {selectedRecord.description}
                </p>
              </div>

              {/* Details Grid */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground">Date</Label>
                  <p className="text-sm font-medium">
                    {selectedRecord.from.split(" - ")[0]}
                  </p>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground flex items-center gap-1.5">
                    <Clock className="h-3.5 w-3.5" />
                    Hours
                  </Label>
                  <p className="text-sm font-medium">{selectedRecord.hours}</p>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground">From</Label>
                  <p className="text-sm font-medium">
                    {selectedRecord.from.split(" - ")[1]}
                  </p>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground">To</Label>
                  <p className="text-sm font-medium">
                    {selectedRecord.to.split(" - ")[1]}
                  </p>
                </div>
              </div>

              {/* Submitted On */}
              <div className="space-y-2 pt-2 border-t">
                <Label className="text-xs text-muted-foreground">
                  Submitted On
                </Label>
                <p className="text-sm">
                  {formatSubmittedDateTime(selectedRecord.submissionDate)}
                </p>
              </div>

              {/* Submitted On */}
              <div className="space-y-2 pt-2 border-t">
                <Label className="text-xs text-muted-foreground">
                  Submitted On
                </Label>
                <p className="text-sm">
                  {formatSubmittedDateTime(selectedRecord.submissionDate)}
                </p>
              </div>

              {/* Supervisor Note - Show for Forwarded, Approved, and Re-Apply statuses */}
              {(selectedRecord.status === "Forwarded" ||
                selectedRecord.status === "Approved" ||
                selectedRecord.status === "Re-Apply") &&
                selectedRecord.supervisorNote &&
                !selectedRecord.isDirectManagerAction && (
                  <Card
                    className={
                      selectedRecord.status === "Forwarded" ||
                      selectedRecord.status === "Approved"
                        ? "bg-blue-50 border-blue-200"
                        : "bg-orange-50 border-orange-200"
                    }
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start gap-3 mb-3">
                        <div
                          className={`h-10 w-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                            selectedRecord.status === "Forwarded" ||
                            selectedRecord.status === "Approved"
                              ? "bg-blue-200"
                              : "bg-orange-200"
                          }`}
                        >
                          <User
                            className={`h-5 w-5 ${
                              selectedRecord.status === "Forwarded" ||
                              selectedRecord.status === "Approved"
                                ? "text-blue-700"
                                : "text-orange-700"
                            }`}
                          />
                        </div>
                        <div className="flex-1">
                          <h4 className="font-semibold text-sm">Supervisor</h4>
                          <p className="text-xs text-muted-foreground">
                            {selectedRecord.supervisorEmail}
                          </p>
                        </div>
                      </div>
                      <div className="ml-13">
                        <p
                          className={`text-xs font-medium mb-1 ${
                            selectedRecord.status === "Forwarded" ||
                            selectedRecord.status === "Approved"
                              ? "text-blue-700"
                              : "text-orange-700"
                          }`}
                        >
                          {selectedRecord.status === "Forwarded"
                            ? "Forwarded Reason:"
                            : selectedRecord.status === "Approved"
                              ? "Supervisor's Note:"
                              : "Supervisor's Request:"}
                        </p>
                        <p className="text-sm">
                          {selectedRecord.supervisorNote}
                        </p>
                        {selectedRecord.supervisorTimestamp && (
                          <p className="text-xs text-muted-foreground mt-2">
                            {formatNoteTimestamp(
                              selectedRecord.supervisorTimestamp,
                            )}
                          </p>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                )}

              {/* Manager Note Display - For Approved (non-direct) */}
              {selectedRecord.status === "Approved" &&
                selectedRecord.managerNote &&
                !selectedRecord.isDirectManagerAction && (
                  <Card className="bg-green-50 border-green-200">
                    <CardContent className="p-4">
                      <div className="flex items-start gap-3 mb-3">
                        <div className="h-10 w-10 rounded-full bg-green-200 flex items-center justify-center flex-shrink-0">
                          <User className="h-5 w-5 text-green-700" />
                        </div>
                        <div className="flex-1">
                          <h4 className="font-semibold text-sm">Manager</h4>
                          <p className="text-xs text-muted-foreground">
                            {selectedRecord.managerEmail}
                          </p>
                        </div>
                      </div>
                      <div className="ml-13">
                        <p className="text-xs font-medium mb-1 text-green-700">
                          Manager's Approval Note:
                        </p>
                        <p className="text-sm">{selectedRecord.managerNote}</p>
                        {selectedRecord.managerTimestamp && (
                          <p className="text-xs text-muted-foreground mt-2">
                            {formatNoteTimestamp(
                              selectedRecord.managerTimestamp,
                            )}
                          </p>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                )}

              {/* Direct Manager Approval */}
              {selectedRecord.status === "Approved" &&
                selectedRecord.isDirectManagerAction &&
                selectedRecord.managerNote && (
                  <Card className="bg-green-50 border-green-200">
                    <CardContent className="p-4">
                      <div className="flex items-start gap-3 mb-3">
                        <div className="h-10 w-10 rounded-full bg-green-200 flex items-center justify-center flex-shrink-0">
                          <User className="h-5 w-5 text-green-700" />
                        </div>
                        <div className="flex-1">
                          <h4 className="font-semibold text-sm">Manager</h4>
                          <p className="text-xs text-muted-foreground">
                            {selectedRecord.managerEmail}
                          </p>
                        </div>
                      </div>
                      <div className="ml-13">
                        <p className="text-xs font-medium mb-1 text-green-700">
                          Approval Note:
                        </p>
                        <p className="text-sm">{selectedRecord.managerNote}</p>
                        {selectedRecord.managerTimestamp && (
                          <p className="text-xs text-muted-foreground mt-2">
                            {formatNoteTimestamp(
                              selectedRecord.managerTimestamp,
                            )}
                          </p>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                )}

              {/* Rejection Notes */}
              {selectedRecord.status === "Rejected" && (
                <>
                  {/* Direct Manager Rejection */}
                  {selectedRecord.isDirectManagerAction &&
                    selectedRecord.managerNote && (
                      <Card className="bg-red-50 border-red-200">
                        <CardContent className="p-4">
                          <div className="flex items-start gap-3 mb-3">
                            <div className="h-10 w-10 rounded-full bg-red-200 flex items-center justify-center flex-shrink-0">
                              <User className="h-5 w-5 text-red-700" />
                            </div>
                            <div className="flex-1">
                              <h4 className="font-semibold text-sm">Manager</h4>
                              <p className="text-xs text-muted-foreground">
                                {selectedRecord.managerEmail}
                              </p>
                            </div>
                          </div>
                          <div className="ml-13">
                            <p className="text-xs font-medium mb-1 text-red-700">
                              Rejection Note:
                            </p>
                            <p className="text-sm">
                              {selectedRecord.managerNote}
                            </p>
                            {selectedRecord.managerTimestamp && (
                              <p className="text-xs text-muted-foreground mt-2">
                                {formatNoteTimestamp(
                                  selectedRecord.managerTimestamp,
                                )}
                              </p>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    )}

                  {/* Supervisor Note (when involved) */}
                  {!selectedRecord.isDirectManagerAction &&
                    selectedRecord.supervisorNote && (
                      <Card
                        className={
                          selectedRecord.isDirectSupervisorAction
                            ? "bg-red-50 border-red-200"
                            : "bg-blue-50 border-blue-200"
                        }
                      >
                        <CardContent className="p-4">
                          <div className="flex items-start gap-3 mb-3">
                            <div
                              className={`h-10 w-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                                selectedRecord.isDirectSupervisorAction
                                  ? "bg-red-200"
                                  : "bg-blue-200"
                              }`}
                            >
                              <User
                                className={`h-5 w-5 ${
                                  selectedRecord.isDirectSupervisorAction
                                    ? "text-red-700"
                                    : "text-blue-700"
                                }`}
                              />
                            </div>
                            <div className="flex-1">
                              <h4 className="font-semibold text-sm">
                                Supervisor
                              </h4>
                              <p className="text-xs text-muted-foreground">
                                {selectedRecord.supervisorEmail}
                              </p>
                            </div>
                          </div>
                          <div className="ml-13">
                            <p
                              className={`text-xs font-medium mb-1 ${
                                selectedRecord.isDirectSupervisorAction
                                  ? "text-red-700"
                                  : "text-blue-700"
                              }`}
                            >
                              {selectedRecord.isDirectSupervisorAction
                                ? "Rejection Reason:"
                                : "Forwarded Reason:"}
                            </p>
                            <p className="text-sm">
                              {selectedRecord.supervisorNote}
                            </p>
                            {selectedRecord.supervisorTimestamp && (
                              <p className="text-xs text-muted-foreground mt-2">
                                {formatNoteTimestamp(
                                  selectedRecord.supervisorTimestamp,
                                )}
                              </p>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    )}

                  {/* Manager Rejection Note (when supervisor also involved) */}
                  {!selectedRecord.isDirectManagerAction &&
                    selectedRecord.managerNote && (
                      <Card className="bg-red-50 border-red-200">
                        <CardContent className="p-4">
                          <div className="flex items-start gap-3 mb-3">
                            <div className="h-10 w-10 rounded-full bg-red-200 flex items-center justify-center flex-shrink-0">
                              <User className="h-5 w-5 text-red-700" />
                            </div>
                            <div className="flex-1">
                              <h4 className="font-semibold text-sm">Manager</h4>
                              <p className="text-xs text-muted-foreground">
                                {selectedRecord.managerEmail}
                              </p>
                            </div>
                          </div>
                          <div className="ml-13">
                            <p className="text-xs font-medium mb-1 text-red-700">
                              Manager's Rejection Note:
                            </p>
                            <p className="text-sm">
                              {selectedRecord.managerNote}
                            </p>
                            {selectedRecord.managerTimestamp && (
                              <p className="text-xs text-muted-foreground mt-2">
                                {formatNoteTimestamp(
                                  selectedRecord.managerTimestamp,
                                )}
                              </p>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    )}
                </>
              )}

              {/* Manager Note Input - Show for Forwarded or Pending status */}
              {(selectedRecord.status === "Forwarded" ||
                selectedRecord.status === "Pending") && (
                <div className="space-y-2 pt-2">
                  <Label
                    htmlFor="manager-note"
                    className="flex items-center gap-2"
                  >
                    <FileText className="h-4 w-4 text-muted-foreground" />
                    Manager Note <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="manager-note"
                    placeholder="Add your note here (required)"
                    value={managerNote}
                    onChange={(e) => setManagerNote(e.target.value)}
                  />
                </div>
              )}
            </div>
          )}

          {/* Action Buttons - Show for Forwarded or Pending status */}
          {selectedRecord &&
            (selectedRecord.status === "Forwarded" ||
              selectedRecord.status === "Pending") && (
              <DialogFooter className="border-t pt-4">
                <div className="flex gap-3 w-full sm:w-auto">
                  <Button
                    onClick={handleApprove}
                    disabled={!managerNote.trim() || isApproving || isRejecting}
                    className="flex-1 sm:flex-none bg-blue-500 hover:bg-blue-600"
                  >
                    {isApproving ? (
                      <>
                        <div className="h-4 w-4 animate-spin rounded-full border-2 border-background border-t-transparent mr-2"></div>
                        Approving...
                      </>
                    ) : (
                      "Approve"
                    )}
                  </Button>
                  <Button
                    onClick={handleReject}
                    disabled={!managerNote.trim() || isApproving || isRejecting}
                    variant="destructive"
                    className="flex-1 sm:flex-none"
                  >
                    {isRejecting ? (
                      <>
                        <div className="h-4 w-4 animate-spin rounded-full border-2 border-background border-t-transparent mr-2"></div>
                        Rejecting...
                      </>
                    ) : (
                      "Reject"
                    )}
                  </Button>
                </div>
              </DialogFooter>
            )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
